import { supabase } from "../supabaseClient";

export interface PlayerProfile {
  id: string;
  user_id: string;
  linked_user_id?: string | null;
  name: string;
  nickname?: string;
  role: "Batsman" | "Bowler" | "All-rounder" | "Keeper";
  batting_style: "RHB" | "LHB";
  bowling_style?: string;
  photo_url?: string;
  jersey_number?: number;
  formats?: string[];
  is_guest: boolean;
  is_archived: boolean;
  created_at: string;
  updated_at: string;
}


export async function listPlayerProfiles(): Promise<PlayerProfile[]> {
  // Get current authenticated user
  const { data: userData, error: authError } = await supabase.auth.getUser();

  if (authError || !userData?.user) {
    if (import.meta.env.DEV) {
      console.log("[PlayerLoad] No authenticated user");
    }
    return [];
  }

  const userId = userData.user.id;

  if (import.meta.env.DEV) {
    console.log("[PlayerLoad] Loading player profiles for user:", userId);
  }

  const { data, error } = await supabase
    .from("player_profiles")
    .select("*")
    .eq("user_id", userId)
    .neq("is_archived", true)
    .order("created_at", { ascending: false });

  if (error) {
    console.error("[PlayerLoad] ERROR loading player profiles:", error);
    return [];
  }

  if (import.meta.env.DEV) {
    console.log("[PlayerLoad] ✅ Loaded player profiles:", data?.length || 0);
    if (data && data.length > 0) {
      console.log("[PlayerLoad] Player names:", data.map(p => p.name).join(", "));
    }
  }

  return data || [];
}

export async function listArchivedPlayerProfiles(): Promise<PlayerProfile[]> {
  const { data: userData, error: authError } = await supabase.auth.getUser();

  if (authError || !userData?.user) {
    return [];
  }

  const userId = userData.user.id;

  const { data, error } = await supabase
    .from("player_profiles")
    .select("*")
    .eq("user_id", userId)
    .eq("is_archived", true)
    .order("created_at", { ascending: false });

  if (error) {
    console.error("[PlayerLoad] ERROR loading archived player profiles:", error);
    return [];
  }

  return data || [];
}

export async function getPlayerProfile(id: string): Promise<PlayerProfile | null> {
  const { data, error } = await supabase
    .from("player_profiles")
    .select("*")
    .eq("id", id)
    .maybeSingle();

  if (error) {
    console.error("Error loading player profile:", error);
    return null;
  }

  return data;
}

export async function createPlayerProfile(
  profile: any
): Promise<PlayerProfile | null> {
  // VALIDATION: Full Name is required
  if (!profile.name || !profile.name.trim()) {
    if (import.meta.env.DEV) {
      console.error("[PlayerSave] Validation failed: Full Name is required");
    }
    throw new Error("Full Name is required");
  }

  // Get current user and validate session
  const { data: userData, error: authError } = await supabase.auth.getUser();

  if (authError) {
    console.error("[PlayerSave] Auth error:", authError);
    throw new Error(`Session error: ${authError.message}`);
  }

  if (!userData?.user) {
    console.error("[PlayerSave] No authenticated user - session expired");
    throw new Error("Session expired. Please log in again.");
  }

  const user = userData.user;

  // HARDCODED PAYLOAD - ONLY valid DB columns
  const payload: any = {
    user_id: user.id,
    name: profile.name.trim(),
    role: profile.role || "Batsman",
    batting_style: profile.batting_style || "RHB",
    is_guest: profile.is_guest ?? false,
    formats: Array.isArray(profile.formats) ? profile.formats : [],
  };

  // Add optional fields ONLY if they exist
  if (profile.nickname && profile.nickname.trim()) {
    payload.nickname = profile.nickname.trim();
  }
  if (profile.bowling_style && profile.bowling_style.trim()) {
    payload.bowling_style = profile.bowling_style.trim();
  }
  if (profile.photo_url && profile.photo_url.trim()) {
    payload.photo_url = profile.photo_url.trim();
  }
  if (profile.jersey_number !== undefined && profile.jersey_number !== null && profile.jersey_number > 0) {
    payload.jersey_number = profile.jersey_number;
  }

  console.log("[FINAL PLAYER PAYLOAD]", payload);
  console.log("[FINAL PLAYER PAYLOAD KEYS]", Object.keys(payload));

  // Attempt insert
  const { data, error } = await supabase
    .from("player_profiles")
    .insert([payload])
    .select("id, user_id, name, nickname, role, batting_style, bowling_style, photo_url, jersey_number, formats, is_guest, is_archived, created_at, updated_at, linked_user_id")
    .single();

  if (import.meta.env.DEV) {
    console.log("[PlayerSave] response:", { data, error });
  }

  if (error) {
    console.error("[PlayerSave] ❌ DATABASE ERROR:", {
      code: error.code,
      message: error.message,
      details: error.details,
      hint: error.hint,
    });

    if (import.meta.env.DEV) {
      console.error("[PlayerSave] Failed payload was:", payload);
    }

    throw new Error(`Failed to save player: ${error.message}`);
  }

  if (!data) {
    console.error("[PlayerSave] ❌ No data returned from insert");
    throw new Error("Failed to save player: No data returned");
  }

  if (import.meta.env.DEV) {
    console.log("[PlayerSave] ✅ Successfully created player profile:", data.id);
    console.log("[PlayerSave] ✅ Saved data:", data);
  }

  // Immediately re-fetch all player profiles for this user to confirm persistence
  const { data: allProfiles, error: fetchError } = await supabase
    .from("player_profiles")
    .select("*")
    .eq("user_id", user.id)
    .order("created_at", { ascending: false });

  if (fetchError) {
    console.error("[PlayerSave] Error re-fetching profiles:", fetchError);
  } else if (import.meta.env.DEV) {
    console.log("[PlayerSave] ✅ Re-fetch confirmed. Total profiles for user:", allProfiles?.length || 0);
    if (allProfiles && allProfiles.length > 0) {
      console.log("[PlayerSave] Player names:", allProfiles.map(p => p.name).join(", "));
    }
  }

  return data;
}

export async function updatePlayerProfile(
  id: string,
  updates: Partial<Omit<PlayerProfile, "id" | "user_id" | "created_at" | "updated_at">>
): Promise<PlayerProfile | null> {
  if (import.meta.env.DEV) {
    console.log("[PlayerUpdate] Updating player:", id, updates);
  }

  const { data, error } = await supabase
    .from("player_profiles")
    .update(updates)
    .eq("id", id)
    .select("id, user_id, name, nickname, role, batting_style, bowling_style, photo_url, jersey_number, formats, is_guest, is_archived, created_at, updated_at, linked_user_id")
    .single();

  if (error) {
    console.error("[PlayerUpdate] ERROR:", {
      code: error.code,
      message: error.message,
      details: error.details,
    });
    throw new Error(`Failed to update player: ${error.message}`);
  }

  if (import.meta.env.DEV) {
    console.log("[PlayerUpdate] ✅ Successfully updated player:", data?.id);
  }

  return data;
}

export async function linkPlayerToUser(
  profileId: string,
  userId: string | null
): Promise<PlayerProfile | null> {
  return updatePlayerProfile(profileId, { linked_user_id: userId });
}

export async function deletePlayerProfile(id: string): Promise<boolean> {
  const { error } = await supabase.from("player_profiles").delete().eq("id", id);

  if (error) {
    console.error("Error deleting player profile:", error);
    return false;
  }

  return true;
}

export async function archivePlayerProfile(id: string): Promise<PlayerProfile | null> {
  return updatePlayerProfile(id, { is_archived: true });
}

export async function restorePlayerProfile(id: string): Promise<PlayerProfile | null> {
  return updatePlayerProfile(id, { is_archived: false });
}


export async function findOrCreatePlayerProfile(name: string): Promise<PlayerProfile | null> {
  const profiles = await listPlayerProfiles();
  const existing = profiles.find(
    (p) => p.name.toLowerCase().trim() === name.toLowerCase().trim()
  );

  if (existing) {
    return existing;
  }

  return await createPlayerProfile({
    name: name.trim(),
    role: "Batsman",
    batting_style: "RHB",
    is_guest: false,
  });
}

export async function searchPlayerProfiles(query: string): Promise<PlayerProfile[]> {
  const { data, error } = await supabase
    .from("player_profiles")
    .select("*")
    .ilike("name", `%${query}%`)
    .neq("is_archived", true)
    .order("name", { ascending: true });

  if (error) {
    console.error("Error searching player profiles:", error);
    return [];
  }

  return data || [];
}

export async function getMyLinkedProfile(userId: string): Promise<PlayerProfile | null> {
  const { data, error } = await supabase
    .from("player_profiles")
    .select("*")
    .eq("linked_user_id", userId)
    .maybeSingle();

  if (error) {
    console.error("Error loading linked profile:", error);
    return null;
  }

  return data;
}

export async function getMyLinkedProfiles(userId: string): Promise<PlayerProfile[]> {
  const { data, error } = await supabase
    .from("player_profiles")
    .select("*")
    .eq("linked_user_id", userId)
    .order("name", { ascending: true });

  if (error) {
    console.error("Error loading linked profiles:", error);
    return [];
  }

  return data || [];
}
